import { createSignal, Show } from 'solid-js';
import { A, useNavigate } from '@solidjs/router';
import { useSFetch } from '../hooks/sfetch';
import { useApi, isOk } from '../api';
import { useUserContext } from '../components/UserContextProvider';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import Text from '../components/Text';
import styles from './Tenants.module.css';
import { firstError } from '../api/envelope';

export default function NewTenant() {
    const sfetch = useSFetch();
    const api = useApi();
    const toast = useToast();
    const navigate = useNavigate();
    const { setUserTenant } = useUserContext();
    const { t } = useTranslation();

    const [name, setName] = createSignal('');
    const [slug, setSlug] = createSignal('');
    const [creating, setCreating] = createSignal(false);

    const valid = () => name().trim().length > 0 && slug().trim().length > 0;

    async function submit(e: Event) {
        e.preventDefault();
        if (!valid()) return;
        setCreating(true);
        try {
            const res = await api.tenantCreate.createTenant({ name: name().trim(), slug: slug().trim() });
            if (isOk(res.data)) {
                toast?.info(t('TENANT_CREATED'));
                const updated = await sfetch('/api/User/tenant');
                if (updated.ok) setUserTenant(updated.value ?? null);
                navigate('/tenant/settings');
            } else {
                toast?.error(firstError(res.data) ?? t('TENANT_CREATE_FAILED'));
            }
        } finally {
            setCreating(false);
        }
    }

    return (
        <div class={styles.formContainer}>
            <A href="/tenant/settings" class={styles.backLink}>
                <i class="fa-solid fa-arrow-left"></i>
                <Text val="TENANT_BACK_TO_SETTINGS" />
            </A>

            <div class={styles.header}>
                <div class={styles.titleSection}>
                    <div class={styles.cardAvatar}>
                        <i class="fa-solid fa-building"></i>
                    </div>
                    <div class={styles.titleText}>
                        <h1><Text val="TENANT_CREATE" /></h1>
                        <p class={styles.subtitle}><Text val="TENANT_CREATE_SUBTITLE" /></p>
                    </div>
                </div>
            </div>

            <form class={styles.formCard} onSubmit={submit}>
                <label class={styles.formField}>
                    <span class={styles.fieldLabel}><Text val="TENANT_NAME" /></span>
                    <input
                        type="text"
                        class={styles.input}
                        placeholder={t('TENANT_NAME')}
                        value={name()}
                        onInput={e => setName(e.currentTarget.value)}
                    />
                </label>

                <label class={styles.formField}>
                    <span class={styles.fieldLabel}><Text val="TENANT_SLUG" /></span>
                    <input
                        type="text"
                        class={styles.input}
                        placeholder={t('TENANT_SLUG_PLACEHOLDER')}
                        value={slug()}
                        onInput={e => setSlug(e.currentTarget.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    />
                    <span class={styles.fieldHelp}><Text val="TENANT_SLUG_HELP" /></span>
                </label>

                <div class={styles.formActions}>
                    <A href="/tenant/settings" class="btn btn-secondary"><Text val="CANCEL" /></A>
                    <button type="submit" class="btn btn-primary" disabled={!valid() || creating()}>
                        <Show when={creating()} fallback={<i class="fa-solid fa-plus"></i>}>
                            <i class="fa-solid fa-spinner fa-spin"></i>
                        </Show>
                        <Text val={creating() ? 'CREATING' : 'TENANT_CREATE'} />
                    </button>
                </div>
            </form>
        </div>
    );
}
